import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatBadgeModule } from '@angular/material/badge';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import { BookComponent, BookUIData } from './book/book.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { SearchService, CategoryItem } from '../../../../shared/services/apis/platform/search.service';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { SearchRequest } from '../../../../shared/services/requests/platform/search.request';
import { MenuComponent } from '../../../../shared/components/menu/menu.component';
import { SearchResponse } from '../../../../shared/services/responses/platform/search.response';
import { FooterComponent } from "../../../../shared/components/footer/footer.component";

@Component({
  selector: 'app-search-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MenuComponent,
    BookComponent,
    EmptyListComponent,
    MatSliderModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatBadgeModule,
    FooterComponent
  ],
  templateUrl: './search-list.component.html',
  styleUrls: ['./search-list.component.css']
})
export class SearchListComponent implements OnInit, OnDestroy {

  public results: BookUIData[] = [];

  // Estado da UI
  public isLoading = true;
  public isLoadingMore = false;
  public allResultsLoaded = false;
  public showFilters = false;

  // Parâmetros de busca e paginação
  public searchTerm: string = '';
  private idBookSelected: number | null = null;
  private currentPage = 1;
  private readonly resultsPerPage = 30;
  private destroy$ = new Subject<void>();
  private filterChange$ = new Subject<void>();

  // Modelos para os filtros
  public minPrice: number = 0;
  public maxPrice: number = 500;
  public idKit: number | null = null;
  public filterEbook: boolean = false;
  public filterImpresso: boolean = false;

  // Novos filtros
  public filterIsbn: string = '';
  public filterAuthorName: string = '';
  public filterCategories: number[] = [];
  public categories: CategoryItem[] = [];

  get activeFilterCount(): number {
    let count = 0;
    if (this.filterEbook) count++;
    if (this.filterImpresso) count++;
    if (this.filterIsbn) count++;
    if (this.filterAuthorName) count++;
    if (this.filterCategories.length > 0) count++;
    if (this.minPrice > 0 || this.maxPrice < 500) count++;
    return count;
  }

  @ViewChild(MenuComponent) private menuComponent!: MenuComponent;

  constructor(
    private searchService: SearchService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router
  ) { }



  ngOnInit(): void {
    this.loadCategories();

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.searchTerm = params['termo'] || '';
        this.idBookSelected = params['idBookSelected'] ? +params['idBookSelected'] : null;
        this.minPrice = params['preco_min'] ? +params['preco_min'] : 0;
        this.maxPrice = params['preco_max'] ? +params['preco_max'] : 200;

        const tipos = params['tipo']?.split(',') || [];
        this.filterEbook = tipos.includes('ebook');
        this.filterImpresso = tipos.includes('impresso');
        this.idKit = tipos.includes('idKit');

        this.filterIsbn = params['isbn'] || '';
        this.filterAuthorName = params['autor'] || '';
        this.filterCategories = params['categorias'] ? params['categorias'].split(',').map(Number) : [];

        this.resetAndSearch();
      });

    this.filterChange$.pipe(
      debounceTime(500), // Aguarda 500ms após a última mudança para evitar buscas excessivas
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateUrlAndSearch();
    });
  }

  ngAfterViewInit(): void {
    if (this.menuComponent)
      this.menuComponent.searchFormControl.setValue(this.searchTerm);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterChange(): void {
    this.filterChange$.next();
  }

  private loadCategories(): void {
    this.searchService.getCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CategoryItem[]) => {
          this.categories = data;
        },
        error: (err) => {
          console.error('Erro ao carregar categorias', err);
        }
      });
  }

  private resetAndSearch(): void {
    this.currentPage = 1;
    this.results = [];
    this.allResultsLoaded = false;
    this.fetchBooks();
  }

  private updateUrlAndSearch(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.getQueryParams(),
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private fetchBooks(loadMore = false): void {
    if (!loadMore) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    const request: SearchRequest = {
      text: this.searchTerm,
      idBookSelected: this.idBookSelected,
      includesEbook: this.filterEbook,
      includesBook: this.filterImpresso,
      page: this.currentPage,
      minPrice: this.minPrice,
      maxPrice: this.maxPrice >= 500 ? 99999 : this.maxPrice,
      idKit: this.idKit,
      isbn: this.filterIsbn,
      authorName: this.filterAuthorName,
      categories: this.filterCategories
    };

    this.searchService.get(request)
      .subscribe({
        next: (response: SearchResponse) => {
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
            this.isLoading = false;
            return;
          }

          if (response && response.results) {
            // Mapeia a resposta da API, adicionando dados mocados para UI
            const newResults = response.results.map(item => ({
              ...item,
              coverImage: `https://placehold.co/400x600/${this.getRandomColor()}/fff?text=${encodeURIComponent(item.bookTitle || item.kitName || '')}`
            }));

            if (loadMore) {
              this.results = [...this.results, ...newResults];
            } else {
              this.results = newResults;
            }

            if (newResults.length < this.resultsPerPage || this.results.length === response.total) {
              this.allResultsLoaded = true;
            }
          }

          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: err => {
          this.alertService.show('Erro na Busca', 'Não foi possível carregar os livros. Tente novamente mais tarde.', AlertType.error);
          console.error(err);
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
  }

  public loadMoreBooks(): void {
    if (this.isLoadingMore || this.allResultsLoaded) return;
    this.currentPage++;
    this.fetchBooks(true);
  }

  private getQueryParams(): any {
    const params: any = {};
    if (this.searchTerm) params.termo = this.searchTerm;

    const tipos = [];

    if (this.filterEbook)
      tipos.push('ebook');
    if (this.filterImpresso)
      tipos.push('impresso');

    if (tipos.length > 0)
      params.tipo = tipos.join(',');
    else if (tipos.length == 0)
      params.tipo = tipos[0];

    params.preco_min = this.minPrice;
    params.preco_max = this.maxPrice;

    if (this.idKit) params.idKit = this.idKit;

    params.isbn = this.filterIsbn || null;
    params.autor = this.filterAuthorName || null;
    params.categorias = this.filterCategories.length > 0 ? this.filterCategories.join(',') : null;

    return params;
  }

  public toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  private getRandomColor(): string {
    const colors = ['333', '8fbc8f', '4682b4', '6a5acd', 'd2691e'];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}